/**
 * Add Task Modal Component
 * TASK-358: Frontend AddTaskModal Component
 *
 * Modal for creating new tasks and follow-ups with support for:
 * - Task type selection (follow-up vs general task)
 * - Title and description
 * - Due date picker
 * - Priority selection
 * - Client association (optional)
 * - User assignment (optional)
 * - Email reminder toggle
 * - Validation and double-click prevention
 */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  X,
  CheckSquare,
  Phone,
  Mail,
  Calendar,
  FileText,
  MoreHorizontal,
  ClipboardList,
  Search,
  Shield,
  BookOpen,
  Settings,
  Bell,
  User,
  Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  TaskType,
  TaskPriority,
  TaskCreateRequest,
  TaskReminder,
  Task,
  PRIORITY_OPTIONS,
} from '../../types/tasks';
import { taskService, UserListItem, RmOfRecord } from '../../services/taskService';
import { AccountContext } from '../../contexts/AccountContext';  // TASK-925: the creator fallback

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: TaskCreateRequest) => Promise<void>;
  isLoading?: boolean;
  // Optional pre-filled values
  clientId?: string;
  clientName?: string;
  defaultTaskType?: TaskType;
  noteId?: string;
  // Edit mode
  editingTask?: Task | null;
}

// Task type options with icons for better UX
const TASK_TYPE_UI_OPTIONS: {
  value: TaskType;
  label: string;
  icon: React.ReactNode;
  category: 'follow_up' | 'task';
}[] = [
  // Follow-up types
  { value: 'follow_up_call', label: 'Call', icon: <Phone size={16} />, category: 'follow_up' },
  { value: 'follow_up_email', label: 'Email', icon: <Mail size={16} />, category: 'follow_up' },
  { value: 'follow_up_meeting', label: 'Meeting', icon: <Calendar size={16} />, category: 'follow_up' },
  { value: 'follow_up_document', label: 'Document', icon: <FileText size={16} />, category: 'follow_up' },
  { value: 'follow_up_other', label: 'Other', icon: <MoreHorizontal size={16} />, category: 'follow_up' },
  // General task types
  { value: 'task_general', label: 'General', icon: <ClipboardList size={16} />, category: 'task' },
  { value: 'task_review', label: 'Review', icon: <Search size={16} />, category: 'task' },
  { value: 'task_compliance', label: 'Compliance', icon: <Shield size={16} />, category: 'task' },
  { value: 'task_research', label: 'Research', icon: <BookOpen size={16} />, category: 'task' },
  { value: 'task_admin', label: 'Admin', icon: <Settings size={16} />, category: 'task' },
];

/**
 * TASK-925: the same fortnight the server falls back to (`task_ownership.DEFAULT_DUE_DAYS`).
 * Pre-filled rather than left blank so the advisor SEES the date the task will carry and can
 * change it — a silent server default is not a date anyone agreed to.
 */
const DEFAULT_DUE_DAYS = 14;
const defaultDueDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_DUE_DAYS);
  return d.toISOString().split('T')[0];
};

const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  clientId,
  clientName,
  defaultTaskType,
  noteId,
  editingTask,
}) => {
  const isEditMode = !!editingTask;
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState<TaskType>(defaultTaskType || 'task_general');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [enableReminder, setEnableReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('09:00');

  // TASK-370: Assignee state
  const [assigneeUserId, setAssigneeUserId] = useState<string>('');
  // TASK-925: who the server WILL assign this to if the select is left alone. Display only —
  // the rule itself lives in `core/task_ownership.py` and runs on write.
  const [defaultOwner, setDefaultOwner] = useState<RmOfRecord | null>(null);
  // Read the context directly rather than through `useAccount()`, which THROWS outside a
  // provider. This modal is mounted unconditionally by its hosts (it owns its own `isOpen`),
  // so a throwing hook takes down the whole page that merely *can* open it — ClientDetail's
  // routing tests died this way. The id is optional here anyway: it only labels who will own
  // the task, and the rule that actually assigns it runs server-side in `task_ownership.py`.
  // Same reasoning as `useViewerRole` (TASK-923).
  const currentUserId = useContext(AccountContext)?.user?.id;
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Tab state (follow-up vs task)
  const [activeTab, setActiveTab] = useState<'follow_up' | 'task'>(
    defaultTaskType?.startsWith('follow_up') ? 'follow_up' : 'task'
  );

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // TASK-370: Fetch users when modal opens
  useEffect(() => {
    if (isOpen && users.length === 0) {
      setLoadingUsers(true);
      taskService.fetchAccountUsers()
        .then((fetchedUsers) => setUsers(fetchedUsers))
        .finally(() => setLoadingUsers(false));
    }
  }, [isOpen]);

  /**
   * What to tell the advisor about who will own this task.
   *
   * `users` comes from `/api/admin/users`, which needs `users:read` — admin-only. An advisor
   * gets an empty roster, so the RM's id resolves to no name; saying "the client's relationship
   * manager" is then the true statement available, and is more use than an id or silence.
   * `unknown` says nothing at all: a failed lookup is not evidence that the owner is me.
   */
  const ownerLabel = ((): string | null => {
    if (!defaultOwner) return null;
    if (defaultOwner.state === 'unknown') return null;
    if (defaultOwner.state === 'none') return currentUserId ? 'you' : null;
    const named = users.find((u) => u.id === defaultOwner.userId);
    if (named) {
      return named.display_name
        || `${named.first_name} ${named.last_name}`.trim()
        || named.email;
    }
    if (defaultOwner.userId === currentUserId) return 'you';
    // The assignments endpoint already named them; only fall back to the role when it did not.
    return defaultOwner.name || "the client's relationship manager";
  })();
  const ownerIsNamedRm = Boolean(
    defaultOwner?.state === 'resolved'
    && defaultOwner.userId !== currentUserId
    && (defaultOwner.name || users.some((u) => u.id === defaultOwner.userId)),
  );

  // TASK-925: resolve the client's RM of record so the modal can NAME the default owner.
  // Without a client there is no RM and the server falls back to the creator, which is the
  // current user — so that is what gets named.
  useEffect(() => {
    // Not in edit mode: `PUT /api/tasks/{id}` only touches `assignee_user_id` when one is
    // SUPPLIED, so promising an owner on a task that already exists is a promise the update
    // route does not keep — leave the select alone there and the row stays exactly as it was.
    if (!isOpen || editingTask) {
      setDefaultOwner(null);
      return;
    }
    let cancelled = false;
    if (clientId) {
      taskService.fetchClientRmOfRecord(clientId).then((rm) => {
        if (!cancelled) setDefaultOwner(rm);
      });
    } else {
      // No client, so no RM to look up — the server falls back to the creator, which is me.
      setDefaultOwner({ state: 'none' });
    }
    return () => { cancelled = true; };
  }, [isOpen, clientId, editingTask]);

  // Reset or pre-fill form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingTask) {
        // Edit mode: pre-fill with existing task data
        setTitle(editingTask.title || '');
        setDescription(editingTask.description || '');
        const taskTypeValue = (editingTask.task_type as TaskType) || 'task_general';
        setTaskType(taskTypeValue);
        setActiveTab(taskTypeValue.startsWith('follow_up') ? 'follow_up' : 'task');
        // Parse due date (remove time portion if present)
        if (editingTask.due_date) {
          const dueDateStr = editingTask.due_date.split('T')[0];
          setDueDate(dueDateStr);
        } else {
          setDueDate('');
        }
        setPriority((editingTask.priority as TaskPriority) || 'medium');
        // Handle reminder
        if (editingTask.reminder_datetime) {
          setEnableReminder(true);
          const reminderDt = new Date(editingTask.reminder_datetime);
          setReminderDate(reminderDt.toISOString().split('T')[0]);
          setReminderTime(reminderDt.toTimeString().slice(0, 5));
        } else {
          setEnableReminder(false);
          setReminderDate('');
          setReminderTime('09:00');
        }
        // TASK-370: Set assignee
        setAssigneeUserId(editingTask.assignee_user_id || '');
      } else {
        // Create mode: reset to defaults
        setTitle('');
        setDescription('');
        setTaskType(defaultTaskType || 'task_general');
        setActiveTab(defaultTaskType?.startsWith('follow_up') ? 'follow_up' : 'task');
        setDueDate(defaultDueDate());  // TASK-925
        setPriority('medium');
        setEnableReminder(false);
        setReminderDate('');
        setReminderTime('09:00');
        setAssigneeUserId('');
      }
      setErrors({});
      setHasUnsavedChanges(false);
    }
  }, [isOpen, defaultTaskType, editingTask]);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = title.trim() !== '' || description.trim() !== '';
    setHasUnsavedChanges(hasChanges);
  }, [title, description]);

  // When tab changes, update task type to first in that category
  useEffect(() => {
    const firstInCategory = TASK_TYPE_UI_OPTIONS.find((t) => t.category === activeTab);
    if (firstInCategory && !taskType.startsWith(activeTab === 'follow_up' ? 'follow_up' : 'task')) {
      setTaskType(firstInCategory.value);
    }
  }, [activeTab]);

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > 500) {
      newErrors.title = 'Title must be 500 characters or less';
    }

    if (description && description.length > 5000) {
      newErrors.description = 'Description must be 5000 characters or less';
    }

    if (enableReminder) {
      if (!reminderDate) {
        newErrors.reminder = 'Reminder date is required when reminder is enabled';
      } else {
        const reminderDateTime = new Date(`${reminderDate}T${reminderTime}`);
        if (reminderDateTime <= new Date()) {
          newErrors.reminder = 'Reminder must be set for a future date/time';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, description, enableReminder, reminderDate, reminderTime]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Build the request payload
    const taskData: TaskCreateRequest = {
      title: title.trim(),
      task_type: taskType,
      priority,
    };

    // Add optional fields
    if (description.trim()) {
      taskData.description = description.trim();
    }

    if (dueDate) {
      // Convert date to ISO datetime (start of day)
      taskData.due_date = `${dueDate}T00:00:00`;
    }

    if (clientId) {
      taskData.client_id = clientId;
    }

    if (noteId) {
      taskData.note_id = noteId;
    }

    // TASK-370: Add assignee if selected
    if (assigneeUserId) {
      taskData.assignee_user_id = assigneeUserId;
    }

    // Add reminder if enabled
    if (enableReminder && reminderDate) {
      const reminder: TaskReminder = {
        scheduled_at: `${reminderDate}T${reminderTime}:00`,
      };
      taskData.reminder = reminder;
    }

    await onSubmit(taskData);
  };

  // Handle close with unsaved changes warning
  const handleClose = () => {
    if (hasUnsavedChanges && !isLoading) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) return;
    }
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  // Filter task types by active tab
  const visibleTaskTypes = TASK_TYPE_UI_OPTIONS.filter((t) => t.category === activeTab);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <CheckSquare size={20} className="text-[#002B45]" />
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode
                  ? (activeTab === 'follow_up' ? 'Edit Follow-up' : 'Edit Task')
                  : (activeTab === 'follow_up' ? 'Add Follow-up' : 'Add Task')
                }
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Tab Selector */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => setActiveTab('follow_up')}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors
                    ${
                      activeTab === 'follow_up'
                        ? 'bg-white text-[#002B45] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  Follow-up
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('task')}
                  className={`
                    flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors
                    ${
                      activeTab === 'task'
                        ? 'bg-white text-[#002B45] shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }
                  `}
                >
                  Task
                </button>
              </div>

              {/* Task Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {activeTab === 'follow_up' ? 'Follow-up Type' : 'Task Type'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {visibleTaskTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setTaskType(type.value)}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm
                        transition-colors
                        ${
                          taskType === type.value
                            ? 'border-[#002B45] bg-[#002B45]/5 text-[#002B45]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }
                      `}
                    >
                      {type.icon}
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (errors.title) setErrors((prev) => ({ ...prev, title: '' }));
                  }}
                  placeholder={
                    activeTab === 'follow_up'
                      ? 'e.g., Follow up on investment proposal'
                      : 'e.g., Review quarterly report'
                  }
                  maxLength={500}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    ${errors.title ? 'border-red-300' : 'border-gray-300'}
                  `}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Client Info (if provided) */}
              {clientName && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <User size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-600">Client:</span>
                  <span className="text-sm font-medium text-gray-900">{clientName}</span>
                </div>
              )}

              {/* TASK-370: Assign to User */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Users size={14} />
                    Assign to
                  </span>
                </label>
                <select
                  value={assigneeUserId}
                  onChange={(e) => setAssigneeUserId(e.target.value)}
                  disabled={loadingUsers}
                  className="
                    w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    disabled:bg-gray-100 disabled:cursor-not-allowed
                  "
                >
                  {/*
                    TASK-925: this used to say "Unassigned (assign to me)", and it was wrong in
                    both halves — the row was saved with a NULL assignee, and the server now
                    assigns the client's RM of record ahead of the creator. Leaving it selected
                    is what lets that rule run; naming a user here overrides it.
                  */}
                  <option value="">
                    {loadingUsers
                      ? 'Loading users...'
                      : "The client's relationship manager, or me"}
                  </option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name || `${user.first_name} ${user.last_name}`.trim() || user.email}
                    </option>
                  ))}
                </select>
                {/*
                  TASK-925: the default is not blank any more, so say what it IS. The label
                  used to read "Unassigned (assign to me)" and then save NULL — the advisor
                  was told the task had an owner and it had none.
                */}
                {!assigneeUserId && ownerLabel && (
                  <p className="mt-1 text-xs text-gray-600">
                    Will be assigned to <span className="font-medium">{ownerLabel}</span>
                    {ownerIsNamedRm ? ' \u2014 the client\u2019s relationship manager' : ''}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Assigning to another user will send them an email notification
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description) setErrors((prev) => ({ ...prev, description: '' }));
                  }}
                  placeholder="Add more details about this task..."
                  rows={3}
                  maxLength={5000}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm resize-none
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    ${errors.description ? 'border-red-300' : 'border-gray-300'}
                  `}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              {/* Due Date and Priority Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="
                      w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    "
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as TaskPriority)}
                    className="
                      w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                      focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    "
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Email Reminder Toggle */}
              <div className="border-t border-gray-100 pt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableReminder}
                    onChange={(e) => {
                      setEnableReminder(e.target.checked);
                      if (!e.target.checked) {
                        setErrors((prev) => ({ ...prev, reminder: '' }));
                      }
                    }}
                    className="
                      w-4 h-4 rounded border-gray-300 text-[#002B45]
                      focus:ring-[#F7B500] focus:ring-offset-0
                    "
                  />
                  <div className="flex items-center gap-2">
                    <Bell size={16} className="text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Set email reminder
                    </span>
                  </div>
                </label>

                {/* Reminder Date/Time Picker */}
                {enableReminder && (
                  <div className="mt-3 pl-7 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Date</label>
                      <input
                        type="date"
                        value={reminderDate}
                        onChange={(e) => {
                          setReminderDate(e.target.value);
                          if (errors.reminder) setErrors((prev) => ({ ...prev, reminder: '' }));
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        className={`
                          w-full px-2 py-1.5 border rounded text-sm
                          focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                          ${errors.reminder ? 'border-red-300' : 'border-gray-300'}
                        `}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Time</label>
                      <input
                        type="time"
                        value={reminderTime}
                        onChange={(e) => setReminderTime(e.target.value)}
                        className="
                          w-full px-2 py-1.5 border border-gray-300 rounded text-sm
                          focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                        "
                      />
                    </div>
                    {errors.reminder && (
                      <p className="col-span-2 text-sm text-red-600">{errors.reminder}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading
                  ? (isEditMode ? 'Saving...' : 'Creating...')
                  : isEditMode
                    ? 'Save Changes'
                    : (activeTab === 'follow_up' ? 'Create Follow-up' : 'Create Task')
                }
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
